/**
 * A custom dropdown (styled popover listbox) — replaces the native <select>, whose OS-rendered popup
 * overlapped the styled trigger and looked broken. Implements the APG listbox KEYBOARD model: opening
 * moves focus into the list, Arrow/Home/End move the active option, Enter/Space commit. Escape closes and
 * returns focus to the trigger; Tab closes and lets focus move on to the next control. Value-based
 * onChange. Closes on outside-click. Flips above the trigger when it would collide with the viewport edge.
 */

import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/cn";

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  className?: string;
  /** Disable the control (non-interactive, dimmed). */
  disabled?: boolean;
  /** Accessible name for the trigger. */
  "aria-label"?: string;
  /** Trigger height utility (default h-9). */
  size?: "sm" | "md";
}

export function Select({
  value,
  onChange,
  options,
  className,
  size = "md",
  disabled = false,
  ...aria
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const listRef = useRef<HTMLUListElement>(null);
  const listId = useId();
  const selectedIdx = options.findIndex((o) => o.value === value);
  const [activeIdx, setActiveIdx] = useState(selectedIdx < 0 ? 0 : selectedIdx);
  const [placement, setPlacement] = useState<"bottom" | "top">("bottom");
  const selected = options[selectedIdx];

  // On open: move focus into the list onto the selected (or first) option; wire outside-click + Escape.
  useEffect(() => {
    if (!open) return;
    const start = selectedIdx < 0 ? 0 : selectedIdx;
    setActiveIdx(start);
    const raf = requestAnimationFrame(() => optRefs.current[start]?.focus());
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, selectedIdx]);

  // Flip above the trigger when there isn't room below and there's more room above (viewport-edge
  // collision). Runs before paint so the open never flashes at the wrong edge.
  useLayoutEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current;
    const list = listRef.current;
    if (!trigger || !list) return;
    const rect = trigger.getBoundingClientRect();
    const gap = 6; // ~0.35rem
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    setPlacement(
      spaceBelow < list.offsetHeight + gap && spaceAbove > spaceBelow ? "top" : "bottom",
    );
  }, [open]);

  function moveTo(idx: number) {
    setActiveIdx(idx);
    optRefs.current[idx]?.focus();
  }
  function commit(idx: number) {
    const opt = options[idx];
    if (opt) onChange(opt.value);
    setOpen(false);
    triggerRef.current?.focus();
  }

  function onListKeyDown(e: React.KeyboardEvent) {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        moveTo(Math.min(options.length - 1, activeIdx + 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        moveTo(Math.max(0, activeIdx - 1));
        break;
      case "Home":
        e.preventDefault();
        moveTo(0);
        break;
      case "End":
        e.preventDefault();
        moveTo(options.length - 1);
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        commit(activeIdx);
        break;
      case "Tab":
        setOpen(false);
        break;
    }
  }

  function onTriggerKeyDown(e: React.KeyboardEvent) {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      setOpen(true);
    }
  }

  return (
    <div ref={ref} className={cn("relative inline-flex", className)}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={aria["aria-label"]}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onTriggerKeyDown}
        className={cn(
          "inline-flex w-full items-center justify-between gap-2 rounded-(--radius-sm) border border-(--border-input) bg-(--surface) pl-3 pr-2.5 text-(length:--text-sm) text-(--text)",
          size === "sm" ? "h-7 text-(length:--text-xs)" : "h-9",
          "transition-colors hover:border-(--border-strong) focus-visible:border-(--brand) focus-visible:outline-none focus-visible:shadow-[0_0_0_3px_var(--ring)]",
          "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-(--border-input)",
        )}
      >
        <span className="truncate">{selected?.label ?? ""}</span>
        <ChevronDown
          className={cn(
            "size-4 shrink-0 text-(--text-subtle) transition-transform",
            open && "rotate-180",
          )}
          aria-hidden
        />
      </button>

      {open && (
        <ul
          id={listId}
          ref={listRef}
          role="listbox"
          aria-label={aria["aria-label"]}
          onKeyDown={onListKeyDown}
          className={cn(
            "absolute left-0 z-(--z-dropdown) max-h-64 min-w-full overflow-auto rounded-(--radius) border border-(--border) bg-(--surface-raised) p-1 shadow-(--shadow-lg)",
            placement === "bottom" ? "top-[calc(100%+0.35rem)]" : "bottom-[calc(100%+0.35rem)]",
          )}
        >
          {options.map((o, i) => {
            const active = o.value === value;
            return (
              <li key={o.value} role="presentation">
                <button
                  ref={(el) => {
                    optRefs.current[i] = el;
                  }}
                  type="button"
                  role="option"
                  aria-selected={active}
                  tabIndex={-1}
                  onClick={() => commit(i)}
                  className={cn(
                    "flex w-full items-center gap-2 whitespace-nowrap rounded-(--radius-sm) px-2.5 py-1.5 text-left text-(length:--text-sm) transition-colors " +
                      "focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-(--brand)",
                    active
                      ? "bg-(--brand-subtle) font-medium text-(--brand-strong)"
                      : "text-(--text) hover:bg-(--surface-hover)",
                  )}
                >
                  <Check
                    className={cn("size-3.5 shrink-0", active ? "opacity-100" : "opacity-0")}
                    aria-hidden
                  />
                  {o.label}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
